// ShareDialog.tsx
import { Box, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from "react-router-dom";
import { FacebookIcon, FacebookShareButton, RedditIcon, RedditShareButton, TumblrIcon, TumblrShareButton, TwitterIcon, TwitterShareButton, WeiboIcon, WeiboShareButton, WhatsappIcon, WhatsappShareButton } from "react-share";
import { ScoreApi } from "../api/ScoreApi";
import ShareLinkIcon from '../asset/icon-share-link.png';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';

interface ShareDialogProps {
    isOpen: boolean;
    onClose: () => void;
    sentenceScoreId: number;
}

const ShareDialog: React.FC<ShareDialogProps> = ({ isOpen, onClose, sentenceScoreId }: ShareDialogProps) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [shareUrl, setShareUrl] = React.useState('');
    const isShareUrlReady = shareUrl.trim().length > 0;

    // Function to get the share URL from ScoreApi
    const getShareUrl = React.useCallback(async (id: number) => {
        // You would typically make an API call here to get the URL
        ScoreApi.getScoreUrl(id)
            .then(response => {
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Scoring audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                setShareUrl(response.data);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [setShareUrl, setErrorMessage, history, location]);

    React.useEffect(() => {
        getShareUrl(sentenceScoreId);
    }, [sentenceScoreId, getShareUrl])

    const handleCopyShareUrlToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            // Show feedback to the user that the text was copied.
            alert("Link copied to clipboard!"); // Consider using a more user-friendly feedback mechanism
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DialogContent>
                <Stack spacing={1}>
                    <Typography variant="h4">Share with Friends</Typography>
                    <Box sx={{ padding: 2 }}>
                        You can share your progress with your friends. If someone opens this link and later creates an account, you get referral credit toward the leaderboard.
                    </Box>
                    <Stack direction="row" spacing={2} sx={{ display: 'flex', alignItems: 'center' }}>
                        <FacebookShareButton
                            url={shareUrl}
                            hashtag={'#verbaschola'}
                        >
                            <FacebookIcon size={40} round={true} />
                        </FacebookShareButton>
                        <RedditShareButton
                            url={shareUrl}
                            title={'Listen to my chinese'}
                        >
                            <RedditIcon size={40} round={true} />
                        </RedditShareButton>
                        <TumblrShareButton
                            url={shareUrl}
                            title={'Listen to my chinese'}
                            tags={['verbaschola', 'chinese', 'languagelearning']}
                        >
                            <TumblrIcon size={40} round={true} />
                        </TumblrShareButton>
                        <TwitterShareButton
                            url={shareUrl}
                            title={'Listen to my chinese'}
                            hashtags={['verbaschola', 'chinese', 'languagelearning']}
                        >
                            <TwitterIcon size={40} round={true} />
                        </TwitterShareButton>

                        <WeiboShareButton
                            url={shareUrl}
                            title={'Listen to my chinese'}
                        >
                            <WeiboIcon size={40} round={true} />
                        </WeiboShareButton>

                        <WhatsappShareButton
                            url={shareUrl}
                            title={'Listen to my chinese'}
                        >
                            <WhatsappIcon size={40} round={true} />
                        </WhatsappShareButton>

                        <IconButton
                            aria-label="Copy share link"
                            disabled={!isShareUrlReady}
                            onClick={handleCopyShareUrlToClipboard}
                        >
                            <img src={ShareLinkIcon} alt="Link" style={{ maxWidth: '42px', maxHeight: '42px' }} />
                        </IconButton>
                    </Stack>                </Stack>
            </DialogContent>
            <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                {/* Close icon */}
            </IconButton>
        </Dialog>
    );
};

export default ShareDialog;
